"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Stack } from "@mui/material";
import { TourDate } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";
import { useLocale, useTranslations } from "next-intl";
import YearSelector from "./year-selector";
import DepartureDateRow from "./departure-date-row";

interface DatesAndPricingSectionProps {
  tourDates: TourDate[];
  onRequestQuote?: (tourDate: TourDate) => void;
  onViewItinerary?: (tourDate: TourDate) => void;
  onBookNow?: (tourDate: TourDate) => void;
  onDateSelect?: (tourDate: TourDate | null) => void;
  onDateHover?: (tourDate: TourDate) => void;
  hasOnTourAir?: boolean;
  selectedDateId?: string | null;
  customerCareNumber?: string;
}

const DatesAndPricingSection = ({
  tourDates,
  onRequestQuote,
  onViewItinerary,
  onBookNow,
  onDateSelect,
  onDateHover,
  hasOnTourAir = false,
  selectedDateId: controlledSelectedDateId,
  customerCareNumber = undefined
}: DatesAndPricingSectionProps) => {
  const t = useTranslations("PDP");
  const locale = useLocale();
  // Extract unique years from tour dates
  const years = useMemo(
    () =>
      Array.from(new Set(tourDates.map((date) => new Date(date.startDate).getFullYear()))).sort(
        (a, b) => a - b
      ),
    [tourDates]
  );

  const [selectedDateId, setSelectedDateId] = useState<string | null>(
    controlledSelectedDateId ?? null
  );

  const selectedDate = useMemo(
    () => tourDates.find((date) => date.packageDateId === selectedDateId) ?? null,
    [tourDates, selectedDateId]
  );

  const initialYear = useMemo(() => {
    if (selectedDate) {
      return new Date(selectedDate.startDate).getFullYear();
    }
    return years[0] || new Date().getFullYear();
  }, [selectedDate, years]);

  const [selectedYear, setSelectedYear] = useState<number>(initialYear);

  useEffect(() => {
    setSelectedDateId(controlledSelectedDateId ?? null);
  }, [controlledSelectedDateId]);

  useEffect(() => {
    if (selectedDate) {
      const selectedDateYear = new Date(selectedDate.startDate).getFullYear();
      if (selectedDateYear !== selectedYear) {
        setSelectedYear(selectedDateYear);
      }
      return;
    }
    if (!years.includes(selectedYear) && years.length) {
      setSelectedYear(years[0]);
    }
  }, [selectedDate, selectedYear, years]);

  // Filter dates by selected year
  const filteredDates = tourDates.filter((date) => {
    const year = new Date(date.startDate).getFullYear();
    return year === selectedYear;
  });

  const scrollBarVisible: boolean = filteredDates.length >= 6;

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setSelectedDateId(null); // Reset selection when changing year
    onDateSelect?.(null);
  };

  const handleDateSelect = (tourDate: TourDate) => {
    setSelectedDateId(tourDate.packageDateId);
    setSelectedYear(new Date(tourDate.startDate).getFullYear());
    onDateSelect?.(tourDate);
  };

  // Disclaimer text based on whether tour has inter air
  const disclaimerText = hasOnTourAir
    ? `${t("Disclaimer With Air")} ${t("Please select a date below for more details")}`
    : `${t("Disclaimer No Air")} ${t("Please select a date below for more details")}`;

  return (
    <Stack
      id="dates-and-pricing-section"
      spacing={2}
      sx={{
        width: {
          xs: "100%",
          lg: "63.8rem"
        },
        overflowX: "visible"
      }}
      data-testid="dates-and-pricing-section"
      data-track="visibility"
      data-module-name="Dates and Pricing Section"
      data-parent-container-type="pdp"
      data-ranking-algorithm="manual"
      data-module-ranking-position="10"
      component="section"
    >
      {/* Section Header */}
      <Typography
        variant="h2"
        sx={{
          fontSize: {xs:"2.4rem", sm:"2.8rem"},
          fontWeight: 600,
          color: "text.primary",
          lineHeight: "normal"
        }}
        data-testid="dates-and-pricing-section-header"
      >
        {t("Dates & Pricing")}
      </Typography>

      {/* Main Container */}
      <Box
        sx={{
          backgroundColor: "background.default",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "2rem",
          paddingTop: "2.4rem",
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
          maxHeight: "80rem"
        }}
      >
        {/* Header Section */}
        <Stack
          spacing="12px"
          alignItems="center"
          sx={{ paddingX: { xs: "1.6rem", legacyTablet: "2.4rem" }, paddingBottom: "3.2rem" }}
        >
          <Typography
            sx={{
              fontSize: "2rem",
              fontWeight: 600,
              color: "text.primary",
              textAlign: "center"
            }}
            data-testid="dates-and-pricing-title"
          >
            {t("Tour Dates")}
          </Typography>

          {/* Disclaimer */}
          <div>
            <Typography
              sx={{
                fontSize: "1.2rem",
                fontWeight: 500,
                color: "text.primary",
                textAlign: "center",
                maxWidth: "40rem",
                lineHeight: "normal"
              }}
              data-testid="dates-and-pricing-copy"
            >
              {disclaimerText}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.2rem",
                fontWeight: 500,
                color: "text.primary",
                textAlign: "center"
              }}
              data-testid="dates-and-pricing-disclaimer"
            >
              {t("All prices are in USD")}
            </Typography>
          </div>

          {/* Year Selector */}
          <YearSelector years={years} selectedYear={selectedYear} onYearChange={handleYearChange} />
        </Stack>

        {/* Column Headers */}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            paddingX: { xs: "1rem", sm: "3rem" },
            paddingRight: { sm: 0 },
            fontSize: "1.6rem",
            fontWeight: 700,
            color: "secondary.main",
            display: { xs: "none", sm: "flex" }
          }}
        >
          <Stack
            sx={{
              flexDirection: "row"
            }}
          >
            <Box sx={{
              paddingLeft: "2rem",
              width: {sm: "22.4rem", md: "36.4rem", lg: "22.4rem"}
            }}>
              <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, color: "secondary.main" }} data-testid="tour-dates-header">
                {t("DAYS")}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, color: "secondary.main" }} data-testid="tour-price-header">
                 {t("Pricing")}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ textAlign: "right", paddingRight: "3rem" }}>
            <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, color: "secondary.main" }} data-testid="tour-offer-header">
              {t("OFFER")}
            </Typography>
          </Box>
        </Stack>

        {/* Departure Dates List */}
        <Box
          sx={{
            overflowY: "auto",
            overflowX: "visible",
            width: `calc(100% + ${scrollBarVisible ? "0.8rem" : "0"})`,
            "&::-webkit-scrollbar": {
              width: "0.8rem"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "grey.400",
              borderRadius: "1rem"
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "grey.600"
            }
          }}
          data-testid="departure-dates-list"
        >
          {filteredDates.map((tourDate, index) => (
            <DepartureDateRow
              key={tourDate.packageDateId}
              index={index}
              tourDate={tourDate}
              isSelected={selectedDateId === tourDate.packageDateId}
              onSelect={handleDateSelect}
              onRequestQuote={onRequestQuote}
              onViewItinerary={(date) => onViewItinerary?.(date)}
              onBookNow={onBookNow}
              onHover={onDateHover}
              lastItem={index === filteredDates.length - 1}
            />
          ))}
        </Box>
      </Box>

      {/* Footer Text */}
      <Stack spacing={1.2} sx={{ maxWidth: { xs: "100%", legacyDesktop: "63.8rem" } }}>
        <Typography
          sx={{
            fontSize: "1.8rem",
            fontWeight: 600,
            color: "text.primary"
          }}
          data-testid="customer-care-title"
        >
          {t("Not seeing the date you want")}
        </Typography>
        <Typography
          sx={{
            fontSize: "1.6rem",
            fontWeight: 500,
            color: "text.primary"
          }}
          data-testid="customer-care-copy"
        >
          <Typography
            component="a"
            sx={{
              fontSize: "1.6rem",
              fontWeight: 700,
              textDecoration: "underline!important",
              cursor: "pointer",
              color: "text.primary"
            }}
            data-testid="call-customer-care-team"
            href={customerCareNumber ? `tel:${customerCareNumber}` : undefined}
          >
            {t("Call our")} {t("Customer Care Team")}
          </Typography>{" "}
          {t(
            "to inquire about dates beyond what's currently listed and to make an advanced reservation"
          )}{" "}
          <Typography
            component="a"
            href={`/${locale}/about-collette/terms-and-conditions#release`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "1.6rem",
              fontWeight: 700,
              textDecoration: "underline !important",
              color: "text.primary",
              cursor: "pointer"
            }}
            data-testid="terms-and-conditions"
          >
            {t("full terms and conditions here")}
          </Typography>
        </Typography>
      </Stack>
    </Stack>
  );
};

export default DatesAndPricingSection;
